import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
    BehaviorSubject,
    map,
    Observable,
    of,
    ReplaySubject,
    switchMap,
    tap,
    throwError,
} from 'rxjs';
import { User } from 'app/core/user/user.types';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';
import { Constants } from 'app/shared/constants';
import { Pagination } from '../pagination/pagination.types';
import { LocalStorageService } from '../local/local-storage.service';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    pageSize: any = Constants.pageLimit;
    timezone: BehaviorSubject<string> = new BehaviorSubject('');
    private _user: ReplaySubject<User> = new ReplaySubject<User>(1);

    // Private
    private _pagination: BehaviorSubject<Pagination | null> =
        new BehaviorSubject(null);
    private _users: BehaviorSubject<User[] | null> =
        new BehaviorSubject(null);
    private userD;
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _localService: LocalStorageService,
        private _router: Router
    ) {
        this.user$.subscribe((user: any) => {
            this.userD = user;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for pagination
     */
    get pagination$(): Observable<Pagination> {
        return this._pagination.asObservable();
    }

    /**
     * Getter for customers
     */
    get users$(): Observable<User[]> {
        return this._users.asObservable();
    }

    get user$(): Observable<User> {
        return this._user.asObservable();
    }

    /**
     * Setter & getter for user
     *
     * @param value
     */
    set user(value: User) {
        // Store the value
        this._user.next(value);
        this._localService.setItem('user', value);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get the current logged in user data
     */
    get(): Observable<User> {
        this.user = this._localService.getItem('user');
        this._user.next(this._localService.getItem('user'));
        if (!this._localService.getItem('user')) {
            this._router.navigate(['/sign-in']);
        }
        return of(this._localService.getItem('user'));
    }

    /**
     * Update the user
     *
     * @param user
     */

    update(formData: User): Observable<any> {
        const obj = {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            contact_no: formData.contact_no,
            // login_id: this.userD._id,
            // login_role: this.userD.role_id,
        };
        if (formData.role_id) {
            obj['role_id'] = formData.role_id;
        }
        return this._httpClient
            .patch<User>(
                environment.apiBaseUrl + '/users/' + this.userD.id,
                obj
            )
            .pipe(
                map((response: any) => {
                    this._user.next(response.user);
                    return response;
                })
            );
    }

    /**
     * Update user profile image
     *
     * @param obj
     */
    uploadImage(obj): Observable<any> {
        return this._httpClient
            .post(environment.apiBaseUrl + '/users/profile/upload-image', obj)
            .pipe(map((data: any) => data));
    }

    /**
     * Update user password
     *
     * @param obj
     */

    changePassword(formData): Observable<any> {
        const obj = {
            userId: this.userD.id,
            password: formData.newPassword,
            oldpassword: formData.currentPassword,
            // login_id: this.userD._id,
            // login_role: this.userD.role_id,
        };
        return this._httpClient
            .post(environment.apiBaseUrl + '/auth/change-password', obj)
            .pipe(
                switchMap((response: any) => {
                    if (response.status === 200) {
                        return of(response);
                    } else {
                        return throwError(response);
                    }
                })
            );
    }

    /**
     * Get customers
     *
     *
     * @param page
     * @param size
     * @param sort
     * @param order
     * @param search
     */
    getUsers(
        page: number = 1,
        size: number = this.pageSize,
        sort: string = 'createdAt',
        order: 'asc' | 'desc' | '' = 'desc',
        search: string = '',
        filterQuery: any = {}
    ): Observable<{ pagination: Pagination; users: User[] }> {
        return this._httpClient
            .get<{ pagination: Pagination; users: User[] }>(
                environment.apiBaseUrl + '/users',
                {
                    params: {
                        page: page,
                        limit: size,
                        sortBy: `${sort}:${order || 'desc'}`,
                        search,
                        ...filterQuery,
                    },
                }
            ).pipe(
                tap((response) => {
                    this._pagination.next(response.pagination);
                    this._users.next(response.users);
                })
            );
    }

    /**
     * Activate / deactivate the user
     *
     * @params userId
     */
    changeUserStatus(id, action_type): Observable<any> {
        const obj = {
            action_type,
        };
        return this._httpClient.post(environment.apiBaseUrl + '/users/' + id, obj)
            .pipe(
                switchMap((response: any) => {
                    if (response.status === 200) {
                        return of(response);
                    } else {
                        return throwError(response);
                    }
                })
            );
    }

    /**
     * Delete the user
     *
     * @params userId
     */
    deleteUser(userId): Observable<any> {
        return this._httpClient.delete(environment.apiBaseUrl + '/users/' + userId)
            .pipe(
                switchMap((response: any) => {
                    if (response.status === 200) {
                        return of(response);
                    } else {
                        return throwError(response);
                    }
                })
            );
    }

    /**
     * Get the user by Id
     */
    getUserById(id: string): Observable<User> {
        return this._httpClient.get<User>(environment.apiBaseUrl + '/users/' + id);
    }

    /**
     * Add Update role
     */
    addUser(formData: User): Observable<User> {
        const obj = {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            contact_no: formData.contact_no,
            role_id: formData.role_id,
            dob: new Date(formData.dob),
            password: formData.password,
            address: formData.address,
            bank_name: formData.bank_name,
            bank_account_no: formData.bank_account_no,
        };
        return this._httpClient
            .post<User>(environment.apiBaseUrl + '/users', obj)
            .pipe(
                switchMap((response: any) => {
                    if (response.status === 200) {
                        return of(response);
                    } else {
                        return throwError(response);
                    }
                })
            );
    }

    /**
     *  Update role
     */
    updateUser(roleId, formData: User): Observable<User> {
        const obj = {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            contact_no: formData.contact_no,
            address: formData.address,
            role_id: formData.role_id,
            dob: new Date(formData.dob),
            password: formData.password,
            bank_name: formData.bank_name,
            bank_account_no: formData.bank_account_no,
        };
        return this._httpClient
            .patch<User>(environment.apiBaseUrl + '/users/' + roleId + '/role', obj)
            .pipe(
                switchMap((response: any) => {
                    if (response.status === 200) {
                        return of(response);
                    } else {
                        return throwError(response);
                    }
                })
            );
    }
}
